import React, { FC, useEffect } from 'react';
import { createGlobalStyle } from 'styled-components';
import { AccountStatus } from 'api/account';
import { User, UserType } from 'api/models';
import { useAccount } from 'hooks/use-account';
import { useUser } from 'hooks/use-user';

declare global {
  interface Window {
    fcWidget: any;
  }
}

const WidgetStyle = createGlobalStyle<{ left: number }>`
  #fc_frame.rl-custom-fc-widget.fc_l2r,
  #fc_frame.fc_l2r {
    left: ${({ left }) => `${left}px`} !important;
  }

  #fc_frame.rl-custom-fc-widget.h-open-container.rl-custom-fc-widget--open {
    opacity: 1;
    visibility: visible;
  }
  #fc_frame.widget-open-animate, #fc_frame.fc-widget-normal.widget-open-animate {
    transform-origin: left bottom !important;
  }
`;

enum Status {
  Unchecked = 'Unchecked',
  NotRequired = 'NotRequired',
  ScriptTagAdded = 'ScriptTagAdded',
  Loaded = 'Loaded',
  Initialized = 'Initialized',
}
enum SourceForFreshchat {
  InProduct = 'in-app',
}
enum AccountStatusForFreshchat {
  Trial = 'trial',
  Paid = 'paid',
}
let status: Status = Status.Unchecked;

const initFreshChat = (user: User, accountStatus?: AccountStatus) => {
  if (status !== Status.Loaded) {
    return;
  }
  status = Status.Initialized;
  window.fcWidget.init({
    token: 'd032d723-fcdc-411d-bcc7-ffc0c81b1c4a',
    host: 'https://wchat.freshchat.com',
    config: {
      headerProperty: {
        direction: 'ltr',
        hideChatButton: true,
      },
      cssNames: {
        widget: 'rl-custom-fc-widget',
        open: 'rl-custom-fc-widget--open',
      },
    },
  });

  window.fcWidget.setExternalId(user.userId);
  window.fcWidget.user.setProperties({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.emailId,
    source: SourceForFreshchat.InProduct,
    status:
      accountStatus === AccountStatus.Trial
        ? AccountStatusForFreshchat.Trial
        : AccountStatusForFreshchat.Paid,
  });
};
const initialize = (user: User, accountStatus?: AccountStatus) => {
  if (status !== Status.Unchecked) {
    return;
  }
  status = Status.ScriptTagAdded;
  const element = document.createElement('script');

  element.id = 'freshchat-js-sdk';
  element.async = true;
  element.onload = () => {
    status = Status.Loaded;
    initFreshChat(user, accountStatus);
  };
  element.src = 'https://wchat.freshchat.com/js/widget.js';
  document.head.appendChild(element);
};

const canShowIcon = (userType: UserType): boolean => {
  const EXTERNAL_DOMAIN_LIST = ['success.rocketlane.com', 'localhost:3000'];
  const { host } = window.location;

  return (
    userType === UserType.Native ||
    userType === UserType.Partner ||
    (UserType.External && EXTERNAL_DOMAIN_LIST.includes(host))
  );
};

export const FCWidget: FC<{ left?: number; render: any }> = ({
  left = 50,
  render,
}) => {
  const { user } = useUser();
  const { status: accountStatus } = useAccount();

  const loadWidget = canShowIcon(user.userType);

  useEffect(() => {
    if (status === Status.Unchecked) {
      if (loadWidget) {
        initialize(user, accountStatus);
      } else {
        status = Status.NotRequired;
      }
    } else if (status === Status.Initialized) {
      status = Status.Loaded;
      initFreshChat(user, accountStatus);
    }
  }, [accountStatus, loadWidget, user]);

  const openFCWidget = () => {
    const fcw = window.fcWidget;

    if (fcw?.isOpen()) {
      fcw?.close();
    } else {
      fcw?.open();
    }
  };

  return (
    <>
      {loadWidget && (
        <>
          <WidgetStyle left={left} />
          {render(openFCWidget)}
        </>
      )}
    </>
  );
};
